/* eslint-disable max-statements */
import Head from 'next/head';
import { View } from 'react-native';
import React, { useEffect } from 'react';
import { isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { GeoblockCheck } from '@24i/nxg-sdk-smartott-shared/src/context/GeoblockCheck';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { ComponentOverridesProvider } from '@24i/nxg-sdk-gluons/src/context/ComponentOverrides';
import { ModalProvider } from '@24i/nxg-sdk-gluons/src/context/Modal';
import { useTheme } from '@24i/nxg-sdk-higgs';
import Theme from '@24i/nxg-sdk-higgs/src/Theme';
import { isWebMobile, isWebView, getTelegramWebApp } from '@24i/nxg-sdk-higgs/src/utils';
import PinProtectionProvider from '@24i/nxg-sdk-pin-protection/src/context/PinProtection';

import type { FooterProps } from '../../components/Footer';
import Footer from '../../components/Footer';
import ReminderModal from '../../components/ReminderModal';
import RootNavigation from '../../navigation/index.web';
import { PodcastPlayerProvider } from '../../context/PodcastPlayerProvider';
import { PodcastPlayerContainer } from '../../context/PodcastPlayerProvider/components/PodcastPlayerContainer';
import { TopGap } from '../../navigation/components/TopGap';
import { extractThemeColor } from './utils';
import { useExpandTeleGramWebApp } from '../../hooks/telegram';
import { IdentityManagerValidation } from '../../components/IdentityManagerValidation';

const Application = ({
    Component,
    pageProps,
    renderFooter,
    topGap,
    renderWhenBlocked = () => null,
}) => {
    const { theme } = useTheme();
    const router = useRouter();
    const { t, i18n: i18nTranslation } = useTranslation();

    useEffect(() => {
        const handleRouteChange = () => {
            // Hack for webviews, scroll rapidly from 1 to 0
            if (isWebView()) {
                window.scrollTo(0, 1);
            }
            window.scrollTo(0, 0);
        };
        router.events.on('routeChangeComplete', handleRouteChange);

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, []);

    useExpandTeleGramWebApp(isTablet);

    const needsTopGap = getTelegramWebApp() && topGap?.showTopGap;
    const backgroundColor = needsTopGap
        ? extractThemeColor(theme.color, topGap?.topGapThemeColor)
        : undefined;

    return (
        // @ts-ignore RTL support from react-native-web
        <View dir={i18nTranslation.dir()} style={{ minWidth: '100vw', minHeight: '100%' }}>
            <Head>
                <title>{t('common.appName.description')}</title>
                <meta
                    name="theme-color"
                    content={theme.color?.background1}
                    // @ts-ignore
                    media="(prefers-color-scheme: dark)"
                />
            </Head>
            <RootNavigation renderFooter={renderFooter}>
                <main>
                    <IdentityManagerValidation />
                    <GeoblockCheck renderWhenBlocked={renderWhenBlocked}>
                        {needsTopGap && <TopGap backgroundColor={backgroundColor} />}
                        <Component {...pageProps} key={router.asPath.split('?')[0]} />
                    </GeoblockCheck>
                </main>
            </RootNavigation>
            {/* @ts-ignore */}
            <style global jsx>{`
                :root {
                    --custom-safe-area-bottom: env(safe-area-inset-bottom, 0px);
                }

                html,
                body,
                div#__next {
                    background-color: ${theme.color?.background1} !important;
                    overscroll-behavior: ${isWebMobile() ? 'none' : 'contain auto'};
                    height: 100%;
                    -webkit-overflow-scrolling: touch;
                }
            `}</style>
        </View>
    );
};

const ThemedApplication = ({ appTheme, componentOverrides, isPageNotFound, ...props }) => {
    const { Component, pageProps, renderFooter, topGap } = props;

    // TODO - was return <Component />; instead of null, but static pages wont build with it
    if (isPageNotFound || !appTheme) return null;

    return (
        <Theme theme={appTheme}>
            <ComponentOverridesProvider components={componentOverrides}>
                <PinProtectionProvider>
                    <ModalProvider>
                        <PodcastPlayerProvider>
                            <ReminderModal />
                            <Application
                                Component={Component}
                                pageProps={pageProps}
                                renderFooter={renderFooter}
                                topGap={topGap}
                                {...props}
                            />
                            <PodcastPlayerContainer />
                        </PodcastPlayerProvider>
                    </ModalProvider>
                </PinProtectionProvider>
            </ComponentOverridesProvider>
        </Theme>
    );
};

export const defaultRenderFooter = (props: FooterProps) => <Footer {...props} />;

ThemedApplication.defaultProps = {
    renderFooter: defaultRenderFooter,
};

export default ThemedApplication;
